import React, { useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { useWorldStore } from '../state/worldStore';
import { useWorldTime, localHour } from '../worldTime';
import { SLICE_DISTRICT } from '../config';
import { useGLTF } from '@react-three/drei';
import { PHASES, pickSkies } from './SkyDome';
import { MODEL_URL } from './CityModel';
import { createSkyMaterial } from '../render/ProceduralSky';
import { useFrame } from '@react-three/fiber';
import { sky } from '../render/skyState';
import { weather } from '../render/weather';

// CityShell — the backdrop the city stands in front of. (File keeps its old
// name; the shape changed.)
//
// THREE LAYERS, and the order is the whole point:
//
//   1. this shell          SKY. No detail to fix on, and no corners.
//   2. the skyline         real 3D buildings, standing in the GAP between the
//                          shell and the model (see CitySkyline).
//   3. the city model
//
// Separating 1 from 2 is what buys parallax. A backdrop with the towers painted
// INTO it slides with the camera as one flat thing and reads as wallpaper; with
// the towers as real geometry in front of it, they shift against the sky as you
// walk and the distance becomes real. That is why the shell carries sky and
// nothing else — any skyline drawn on it would defeat the layer in front.
//
// A SPHERE, not a box. Four walls meet at four vertical seams, and a corner is
// the one thing a backdrop cannot have: it hands the eye a fixed edge to read
// depth against, so the illusion collapses exactly where two skies meet at an
// angle. A sphere has no seam to find, and being equirectangular it takes the
// SkyDome images whole instead of sliced into quadrants.
//
// Tune live: ?boxpad (gap to fill with skyline) · ?box=0 (off).
const qp = (k, d) => {
  const v = typeof window !== 'undefined' && parseFloat(new URLSearchParams(window.location.search).get(k));
  return Number.isFinite(v) ? v : d;
};
const ON = typeof window === 'undefined' || new URLSearchParams(window.location.search).get('box') !== '0';

export default function CityBox({ groundColor = '#1b1622', proceduralSky = false }) {
  // The new look's sky is computed per view direction, so the shell can wear the
  // dome's own material and be indistinguishable from it — no second sky to
  // crossfade in step. One material for both shell layers.
  const skyMat = useMemo(() => (proceduralSky ? createSkyMaterial() : null), [proceduralSky]);
  useEffect(() => () => skyMat?.dispose(), [skyMat]);
  useWorldTime(SLICE_DISTRICT.timezone);   // re-render on the clock tick / ?time
  // The city's OWN road surface, reused for the ground the towers stand on.
  // A flat colour out there read as a void the moment the light caught it at an
  // angle — the tell was that the city had texture and the ground did not.
  // Borrowing the real material keeps the two continuous, and useGLTF has the
  // model cached already so this costs nothing but a texture clone.
  const { scene: city } = useGLTF(MODEL_URL);

  // Clone the whole MATERIAL, not just its texture.
  //
  // Borrowing the map alone put the road's pixels on a meshBasicMaterial, which
  // is unlit — so the outer ground stayed bright at midnight while the city's
  // identical road went dark beside it, and the seam between them was the most
  // obvious thing in the frame. Day/night here is not a tint we apply, it is the
  // scene's lights falling on a lit surface; the only way to match is to be the
  // same kind of surface. Cloning carries roughness, metalness and fog across
  // too, so it responds to the hour exactly as the roads it continues.
  const roadMat = useMemo(() => {
    let src = null;
    city.traverse((o) => {
      if (src || !o.isMesh) return;
      const m = Array.isArray(o.material) ? o.material[0] : o.material;
      if (m?.name === 'RoadsGround' && m.map) src = m;
    });
    if (!src) return null;
    const mat = src.clone();
    // The map must be its own instance: repeat is a property of the TEXTURE, so
    // tiling a shared one would retile the city's actual roads with it.
    mat.map = src.map.clone();
    mat.map.needsUpdate = true;
    mat.map.wrapS = mat.map.wrapT = THREE.RepeatWrapping;
    return mat;
  }, [city]);
  useEffect(() => () => { roadMat?.map?.dispose(); roadMat?.dispose(); }, [roadMat]);
  const bounds = useWorldStore((s) => s.cityBounds);

  // THE CITY GOES ON AFTER DARK. Outside the model the ground is a plain disc
  // under the skyline towers, and at night it was black — from the fly-in the
  // city sat in a void. On the street only (proceduralSky), the disc gets a
  // faint grid of distant street light: avenues and blocks, each cell its own
  // brightness, fading in with the night. It reads as more city from above and
  // through the gaps in the skyline, and costs one texture-free shader branch.
  const glowU = useMemo(() => ({ uGlow: { value: 0 }, uCityMin: { value: new THREE.Vector2() }, uCityMax: { value: new THREE.Vector2() } }), []);
  useEffect(() => {
    if (!roadMat || !proceduralSky) return undefined;
    const prev = roadMat.onBeforeCompile;
    roadMat.onBeforeCompile = (shader) => {
      Object.assign(shader.uniforms, glowU);
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\nvarying vec2 vOuterXZ;')
        .replace('#include <project_vertex>', '#include <project_vertex>\nvOuterXZ = (modelMatrix * vec4(transformed, 1.0)).xz;');
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', `#include <common>
varying vec2 vOuterXZ;
uniform float uGlow; uniform vec2 uCityMin; uniform vec2 uCityMax;
float outerHash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }`)
        .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>
if (uGlow > 0.001) {
  vec2 gp = vOuterXZ;
  float inside = step(uCityMin.x, gp.x) * step(gp.x, uCityMax.x) * step(uCityMin.y, gp.y) * step(gp.y, uCityMax.y);
  vec2 f = fract(gp / 24.0);
  vec2 dl = min(f, 1.0 - f) * 24.0;
  float street = max(1.0 - smoothstep(0.4, 2.2, dl.x), 1.0 - smoothstep(0.4, 2.2, dl.y));
  vec2 fa = fract(gp / 96.0);
  vec2 da = min(fa, 1.0 - fa) * 96.0;
  float avenue = max(1.0 - smoothstep(0.8, 3.5, da.x), 1.0 - smoothstep(0.8, 3.5, da.y));
  float cell = 0.35 + 0.65 * outerHash(floor(gp / 24.0));
  float glow = max(street * cell, avenue * 0.9) * (1.0 - inside);
  totalEmissiveRadiance += vec3(1.0, 0.55, 0.24) * glow * uGlow * 0.3;
}`);
    };
    roadMat.customProgramCacheKey = () => 'cityBoxOuterGlow';
    roadMat.needsUpdate = true;
    return () => { roadMat.onBeforeCompile = prev; roadMat.needsUpdate = true; };
  }, [roadMat, proceduralSky, glowU]);
  useEffect(() => {
    if (!bounds) return;
    glowU.uCityMin.value.set(bounds.min[0], bounds.min[1]);
    glowU.uCityMax.value.set(bounds.max[0], bounds.max[1]);
  }, [bounds, glowU]);
  useFrame(() => {
    glowU.uGlow.value = proceduralSky ? THREE.MathUtils.smoothstep(sky.night, 0.2, 0.7) * (1 + 0.3 * weather.wet) : 0;
  });
  const skylineOuter = useWorldStore((s) => s.skylineOuter);

  // One texture per phase. Same files as SkyDome — already in cache, so this
  // costs nothing new, and on a sphere they need no offsetting at all.
  const tex = useMemo(() => {
    const loader = new THREE.TextureLoader();
    const out = {};
    for (const p of PHASES) {
      const t = loader.load(`/skybox/sky_${p}.jpg`);
      t.colorSpace = THREE.SRGBColorSpace;
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
      t.anisotropy = 4;
      out[p] = t;
    }
    return out;
  }, []);
  useEffect(() => () => Object.values(tex).forEach((t) => t.dispose()), [tex]);

  const { a, b, blend } = pickSkies(localHour(SLICE_DISTRICT.timezone));

  const shell = useMemo(() => {
    if (!bounds) return null;
    // The gap is deliberate and load-bearing: it is where the skyline stands.
    // Too small and the towers are pressed against the sky with nothing to
    // parallax across; too large and you can walk far enough to see them end.
    const pad = qp('boxpad', 120);
    // Radius reaches the footprint's FARTHEST point — its corner — not the
    // nearest edge, or the city would poke out through the shell diagonally.
    const hx = (bounds.max[0] - bounds.min[0]) / 2;
    const hz = (bounds.max[1] - bounds.min[1]) / 2;
    const cx = (bounds.min[0] + bounds.max[0]) / 2;
    const cz = (bounds.min[1] + bounds.max[1]) / 2;
    // Whichever is larger: clearance past the city's corner, or enough room to
    // stand well outside the skyline. The towers reach further than the city
    // does, so sizing off the city alone put them through the wall.
    const fromCity = Math.hypot(hx, hz) + pad;
    const fromSkyline = skylineOuter * 1.25;
    return { r: Math.max(fromCity, fromSkyline), cx, cz };
  }, [bounds, skylineOuter]);

  // THE GROUND, WITH THE STATIONS CUT OUT OF IT.
  //
  // A circle until something needs to sink into it, then the same circle as a
  // Shape with holes subtracted — real geometry, not a trick of render order, so
  // you look down a station and see its stairwell rather than the tarmac that
  // would otherwise be drawn across the top of it.
  //
  // UVs are rewritten to match what CircleGeometry produces. ShapeGeometry's
  // defaults are raw vertex coordinates, which across a disc hundreds of units
  // wide would repeat the road texture into noise — the tiling is set by
  // `?groundtile` against the material, and it can only stay meaningful if the
  // two geometries agree on what a UV of 1 means.
  const pits = useWorldStore((s) => s.groundPits);
  const groundGeo = useMemo(() => {
    if (!shell) return null;
    const R = shell.r * 1.05;
    if (!pits.length) return new THREE.CircleGeometry(R, 64);
    const shape = new THREE.Shape();
    shape.absarc(0, 0, R, 0, Math.PI * 2, false);
    for (const p of pits) {
      // A rectangle, turned to match the station standing in it. Built from
      // corners rather than a Path helper because the hole is rotated, and the
      // same four points are what the stencil mask covers — the two ground
      // layers have to agree on the opening or one of them shows a rim.
      //
      // The mesh is rotated -90° about X, which maps shape +y onto world -z, so
      // every z is negated going in. Getting this wrong puts the hole in the
      // right place mirrored to the wrong side of the city.
      const c = Math.cos(p.rotY || 0); const s = Math.sin(p.rotY || 0);
      const hw = p.w / 2; const hd = p.d / 2;
      const pts = [[-hw, -hd], [hw, -hd], [hw, hd], [-hw, hd]].map(([lx, lz]) => {
        const wx = p.x + lx * c + lz * s;
        const wz = p.z - lx * s + lz * c;
        return [wx - shell.cx, -(wz - shell.cz)];
      });
      const hole = new THREE.Path();
      // Reversed, so the hole winds against the outer circle. Same winding and
      // ShapeGeometry fills it in instead of cutting it out.
      hole.moveTo(pts[3][0], pts[3][1]);
      for (let i = 2; i >= 0; i--) hole.lineTo(pts[i][0], pts[i][1]);
      hole.closePath();
      shape.holes.push(hole);
    }
    // WHICH GROUND IS ACTUALLY UNDER THE STATION.
    //
    // This disc sits at y −0.3, deliberately BELOW the city model's own y=0 base
    // so the city's ground wins wherever the two overlap. That is right for the
    // seam and wrong for a pit: cutting a hole here does nothing at all if the
    // station stands on the model's ground, because the model is still drawing
    // over the top of it. The distances say which case we are in.
    for (const p of pits) {
      const d = Math.hypot(p.x - shell.cx, p.z - shell.cz);
      console.log(`[ground] pit ${p.w}x${p.d} at ${p.x.toFixed(0)},${p.z.toFixed(0)} — ${d.toFixed(0)}u from centre, disc radius ${R.toFixed(0)}`
        + (d > R ? ' — OUTSIDE the disc entirely, nothing to cut' : ''));
    }
    console.log('[ground] if the hole is cut here and you still see tarmac, the station is standing on the CITY MODEL\'s own ground (baked into the GLB at y=0, drawn above this disc) — that layer needs a different cut.');
    const g = new THREE.ShapeGeometry(shape, 64);
    const pos = g.attributes.position;
    const uv = new Float32Array(pos.count * 2);
    for (let i = 0; i < pos.count; i++) {
      uv[i * 2] = pos.getX(i) / (2 * R) + 0.5;
      uv[i * 2 + 1] = pos.getY(i) / (2 * R) + 0.5;
    }
    g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
    return g;
  }, [shell, pits]);

  if (!ON || !shell || !groundGeo) return null;
  const { r, cx, cz } = shell;
  // Repeat count is set here rather than in the memo because it depends on the
  // shell radius, which the memo produces.
  if (roadMat) {
    const tiles = qp('groundtile', Math.max(4, Math.round((r * 2) / 60)));
    if (roadMat.map.repeat.x !== tiles) roadMat.map.repeat.set(tiles, tiles);
  }

  return (
    <group>
      {/* Ground across the WHOLE enclosure, not just under the city. The skyline
          stands out in the gap and would otherwise rise out of nothing. Circular
          now, to match the shell — a rectangle would show its own corners past
          the sphere, for exactly the reason the box did. Sits at −0.3, below the
          model's y=0 base, so the city's own ground still wins. */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[cx, -0.3, cz]} geometry={groundGeo}>
        {/* The city's own road material, so the scene's lights darken it at
            night exactly as they darken the roads it continues — no tint of our
            own, because a tint is what made the seam visible in the first place.
            Falls back to a flat colour only if the material can't be found.
            ?groundtile sets how many times the road repeats across the disc:
            too few and it smears, too many and it turns to noise. */}
        {roadMat
          ? <primitive object={roadMat} attach="material" />
          : <meshBasicMaterial color={groundColor} toneMapped={false} fog={false} />}
      </mesh>

      {/* Inside-out sphere. Base sky opaque, next phase fading over it — matching
          how the dome crossfades, or the shell would snap between skies while the
          sky above it dissolved smoothly. */}
      {skyMat ? (
        <mesh position={[cx, 0, cz]} renderOrder={-9} material={skyMat} frustumCulled={false}>
          <sphereGeometry args={[r, 48, 32]} />
        </mesh>
      ) : (
      <group position={[cx, 0, cz]} renderOrder={-9}>
        <mesh>
          <sphereGeometry args={[r, 48, 32]} />
          <meshBasicMaterial map={tex[a]} side={THREE.BackSide} fog={false} toneMapped={false} depthWrite={false} />
        </mesh>
        {blend > 0 && a !== b && (
          <mesh>
            <sphereGeometry args={[r - 2, 48, 32]} />
            <meshBasicMaterial map={tex[b]} side={THREE.BackSide} transparent opacity={blend} depthWrite={false} fog={false} toneMapped={false} />
          </mesh>
        )}
      </group>
      )}
    </group>
  );
}
